import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronLeft } from 'lucide-react';

interface Volver {
  /** Ruta de regreso, por ejemplo "/mas". */
  a: string;
  /** Texto visible, por ejemplo "Volver" o "Respaldos". */
  texto: string;
  /** Nombre para lectores de pantalla; debe contener el texto visible. */
  etiqueta?: string;
  /** Reemplaza esta pantalla en el historial: "atrás" no regresa a ella. */
  reemplazar?: boolean;
}

interface Props {
  titulo: string;
  descripcion?: string;
  /** Línea corta en mayúsculas sobre el título, como una fecha. */
  antetitulo?: string;
  /** Enlace de regreso de Foco, arriba a la izquierda. */
  volver?: Volver;
  /**
   * Algo a la derecha del título, como la pastilla de la etapa del plan
   * (0.11.0). En pantallas angostas baja debajo del título.
   */
  extra?: ReactNode;
  /** Encabezado compacto (0.11.0): menos aire arriba y abajo, para que quepa más. */
  compacto?: boolean;
  children?: ReactNode;
}

/**
 * Título de pantalla con el estilo de Foco: título grande y con presencia,
 * descripción breve en el color secundario y regreso opcional.
 *
 * Reacomodo de la 0.11.0: con `compacto` el encabezado ocupa menos alto y
 * `extra` deja poner la pastilla de la etapa junto al título, para que lo
 * importante del día quepa sin desplazar.
 */
export function PageHeader({ titulo, descripcion, antetitulo, volver, extra, compacto = false, children }: Props) {
  return (
    <header className={compacto ? 'mb-4 mt-1' : 'mb-[25px] mt-2'}>
      {volver !== undefined && (
        <Link
          to={volver.a}
          replace={volver.reemplazar === true}
          aria-label={volver.etiqueta ?? volver.texto}
          className="-ml-1 mb-2 inline-flex min-h-11 items-center gap-1 pr-3 text-[13px] text-marca active:opacity-60"
        >
          <ChevronLeft className="size-4" />
          {volver.texto}
        </Link>
      )}
      {antetitulo !== undefined && <span className={`antetitulo ${compacto ? 'mb-1' : 'mb-2'}`}>{antetitulo}</span>}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h1
          className={`${
            compacto
              ? 'text-[27px] leading-[1.15] tracking-[-1px] min-[401px]:text-[30px]'
              : 'text-[31px] leading-[1.12] tracking-[-1.3px] min-[401px]:text-[34px]'
          } text-texto`}
        >
          {titulo}
        </h1>
        {extra}
      </div>
      {descripcion !== undefined && (
        <p className={`${compacto ? 'mt-1.5' : 'mt-[11px]'} text-[13px] leading-relaxed text-texto-suave`}>
          {descripcion}
        </p>
      )}
      {children !== undefined && <div className={compacto ? 'pt-2.5' : 'pt-3'}>{children}</div>}
    </header>
  );
}
